import { useEffect, useState } from "react";
import axios from "axios";
import apiUrls from "../../../api";

export interface ProjectGroupWithProjects {
  project_group_id: number;
  project_group_name: string;
  projects: Array<{ project_id: number; project_name: string }>;
}

export interface ScopePickerProjectRaw {
  project_id: number;
  project_name: string;
}

interface ScopePickerPgRaw {
  project_group_id: number;
  project_group_name: string;
  projects: ScopePickerProjectRaw[];
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isScopePickerProjectRaw(value: unknown): value is ScopePickerProjectRaw {
  return (
    isRecord(value) &&
    typeof value.project_id === "number" &&
    typeof value.project_name === "string"
  );
}

function isScopePickerPgRaw(value: unknown): value is ScopePickerPgRaw {
  return (
    isRecord(value) &&
    typeof value.project_group_id === "number" &&
    typeof value.project_group_name === "string" &&
    Array.isArray(value.projects) &&
    value.projects.every(isScopePickerProjectRaw)
  );
}

export const useProjectGroupsWithProjects = () => {
  const [pgList, setPgList] = useState<ProjectGroupWithProjects[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPgs = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get<unknown>(apiUrls.stats.getAllSessionsByProjectGroup);
        if (cancelled) return;
        const body = res.data;
        const raw: unknown[] = Array.isArray(body)
          ? body
          : isRecord(body) && Array.isArray(body.data)
          ? body.data
          : [];
        const mapped: ProjectGroupWithProjects[] = raw.filter(isScopePickerPgRaw).map((pg) => ({
          project_group_id: pg.project_group_id,
          project_group_name: pg.project_group_name,
          projects: pg.projects.map((p) => ({
            project_id: p.project_id,
            project_name: p.project_name,
          })),
        }));
        mapped.sort((a, b) => a.project_group_name.localeCompare(b.project_group_name));
        setPgList(mapped);
      } catch (e: unknown) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "fetch failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchPgs();
    return () => {
      cancelled = true;
    };
  }, []);

  return { pgList, loading, error };
};
